import { useState } from "react"

import { Badge, Flex, Table, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { LoadErrorEmptyTable } from "@/molecules/EmptyTable"
import { ExpandIconTable } from "@/molecules/ExpandIconTable"
import { Heading } from "@/molecules/Heading"
import { AGENT_AI_DISCOVERY_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { PaginationTab } from "@/organisms/PaginationTab"
import {
  MAX_DECISIONS_PAGE_SIZE,
  useAiPolicyDecisions
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies"
import { getAiPolicyKind } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import { RuleCard } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleCard"
import { RuleTitle } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleTitle"
import { ViolationDetails } from "../ViolationDetails"
import { getColumnsTable } from "./getColumnsTable"

import styles from "./ViolationLog.module.scss"

import type { AiPolicyDecisionRow } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.types"
import type { PolicyRecordInput } from "@/pages/GovernancePage/applications/policies/useAiPolicies.types"

// The violations a page of the log shows
const PAGE_SIZE = 10

const getRuleGroups = (policy: PolicyRecordInput | undefined) =>
  policy
    ? [
        ...policy.allowed.map((group) => ({ type: "allow" as const, group })),
        ...policy.notAllowed.map((group) => ({
          type: "notAllow" as const,
          group
        }))
      ]
    : []

// The policy's rules, then a page of its violations, each expanding to its
// details
export const ViolationsList = (props: {
  policyId: string
  policy: PolicyRecordInput | undefined
}) => {
  const { t } = useTranslation()
  const { onRowClick } = useDrawer(AGENT_AI_DISCOVERY_DRAWER_ID)
  const [page, setPage] = useState(0)
  // The API counts no total: the log takes the policy's violations in one
  // page, as many as the API serves, and pages through them here
  const violations = useAiPolicyDecisions(
    { policyId: props.policyId },
    { pageSize: MAX_DECISIONS_PAGE_SIZE }
  )
  const groups = getRuleGroups(props.policy)
  const kind = getAiPolicyKind(props.policy?.live)
  // The decisions do not say which rule group matched: with only one, it is
  // the one every violation matched
  const matchedRule = groups.length === 1 ? groups[0] : undefined
  // Every violation expands to its details; the expand icon reads it from the
  // row, as the other expandable tables' rows say it
  const rows = violations.rows.map((row) => ({ ...row, expandable: true }))
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const getEmptyText = () => {
    if (violations.isLoading) return null
    if (violations.isError)
      return <LoadErrorEmptyTable onRetry={violations.refetch} />

    return t(
      violations.isServed
        ? "governance.ai.live.violationLog.empty"
        : "governance.ai.live.decisions.notServed"
    )
  }

  return (
    <Flex vertical gap={32} className={styles.content}>
      <Flex vertical gap={16}>
        {groups.map(({ type, group }, index) => (
          <RuleCard
            key={`${type}-${index}`}
            prefix={<RuleTitle type={type} kind={kind} />}
            mode="drawer"
            type={type}
            data={group}
            violated={!!props.policy?.violated}
          />
        ))}
      </Flex>
      <Flex vertical gap={12}>
        <Flex gap={8} align="center">
          <Heading
            level={6}
            title={t("governance.ai.live.violationLog.list")}
          />
          <Badge count={rows.length} overflowCount={MAX_DECISIONS_PAGE_SIZE} />
        </Flex>
        {violations.hasNext && (
          <Text type="secondary">
            {t("governance.ai.live.violationLog.latest", {
              count: MAX_DECISIONS_PAGE_SIZE
            })}
          </Text>
        )}
        <Table<AiPolicyDecisionRow>
          rowKey="key"
          loading={violations.isLoading}
          dataSource={pageRows}
          columns={getColumnsTable(t, onRowClick)}
          pagination={false}
          locale={{ emptyText: getEmptyText() }}
          expandable={{
            expandIcon: ExpandIconTable,
            expandRowByClick: true,
            expandedRowRender: (violation) => (
              <ViolationDetails
                violation={violation}
                matchedRule={!!matchedRule}
              />
            )
          }}
        />
      </Flex>
      {/* Always in the drawer's bottom left corner, under a long list too */}
      <div className={styles.pagination}>
        <PaginationTab
          loading={violations.isLoading}
          metadata={{ total: rows.length, page }}
          pageSize={PAGE_SIZE}
          current={page}
          onChange={(next) => setPage(next - 1)}
        />
      </div>
    </Flex>
  )
}
